import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { DEFAULT_IMAGES } from '../constants/defaults';

/**
 * Hook para obtener datos actualizados de un usuario
 * Cache simple para evitar múltiples peticiones del mismo usuario
 */
const userCache = new Map();
const cacheExpiry = 5 * 60 * 1000; // 5 minutos

export const useUserData = (userId) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchUserData = async () => {
      // Verificar cache
      const cached = userCache.get(userId);
      if (cached && Date.now() - cached.timestamp < cacheExpiry) {
        setUserData(cached.data);
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          // URL efectiva del banner: si bannerURL es null o '' (tras eliminar), no usar legacy
          const effectiveBannerURL = (data.bannerURL != null && data.bannerURL !== '')
            ? data.bannerURL
            : (data.bannerURL === undefined && typeof data.banner === 'string' && data.banner.startsWith('http') ? data.banner : undefined);

          const userData = {
            id: userId,
            uid: userId, // mantener por compatibilidad
            ...data,
            displayName: data.displayName || data.username || 'Usuario',
            photoURL: (data.photoURL === null || data.photoURL === '') ? null : (data.photoURL || data.profileImage || DEFAULT_IMAGES.PROFILE),
            username: data.username || '',
            verified: data.verified || false,
            followersCount: data.followersCount || 0,
            followingCount: data.followingCount || 0,
            postsCount: data.postsCount || 0,
            bannerURL: effectiveBannerURL,
            photoURLIsGif: data.photoURLIsGif === true,
            bannerURLIsGif: data.bannerURLIsGif === true,
            profileBackgroundType: data.profileBackgroundType || null,
            profileBackgroundURL: data.profileBackgroundURL || null,
            recentAvatarURLs: Array.isArray(data.recentAvatarURLs) ? data.recentAvatarURLs : [],
            recentBannerURLs: Array.isArray(data.recentBannerURLs) ? data.recentBannerURLs : [],
            premiumNameColor: data.premiumNameColor || null,
            isPremium: data.isPremium === true,
            isAmbassador: data.isAmbassador === true
          };

          // Guardar en cache
          userCache.set(userId, {
            data: userData,
            timestamp: Date.now()
          });

          setUserData(userData);
        } else {
          // Usuario no encontrado, usar datos por defecto
          const defaultData = {
            id: userId,
            uid: userId,
            displayName: 'Usuario',
            photoURL: DEFAULT_IMAGES.PROFILE,
            username: '',
            verified: false,
            followersCount: 0,
            followingCount: 0,
            postsCount: 0,
            premiumNameColor: null,
            isPremium: false
          };
          setUserData(defaultData);
        }
      } catch (error) {
        console.error('Error al obtener datos del usuario:', error);
        setUserData({
          id: userId,
          uid: userId,
          displayName: 'Usuario',
          photoURL: DEFAULT_IMAGES.PROFILE,
          username: '',
          verified: false,
          followersCount: 0,
          followingCount: 0,
          postsCount: 0,
          premiumNameColor: null,
          isPremium: false,
          isAmbassador: false
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  return { userData, loading };
};

/**
 * Función para limpiar el cache (útil cuando un usuario actualiza su perfil)
 */
export const clearUserCache = (userId) => {
  if (userId) {
    userCache.delete(userId);
  } else {
    userCache.clear();
  }
};
